/**
 * BotFitters product card (snippets/bf-product-card.liquid), used on every product grid.
 *
 * Picks a variant from the card's color swatches + dropdowns, swaps the photo to the
 * chosen color, and either adds it to the bag (same bag drawer + count as Horizon) or,
 * for personalized items, opens the product page with that variant selected.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

class BfProductCard extends HTMLElement {
  connectedCallback() {
    if (this.variants) return;
    const json = this.querySelector('script[data-variants]')?.textContent || '[]';
    /** @type {Array<{id:number, options:string[], available:boolean, price:string, cents:number, image:string|null}>} */
    this.variants = JSON.parse(json);
    this.colorIndex = Number(this.dataset.colorIndex);
    this.button = /** @type {HTMLButtonElement} */ (this.querySelector('[data-cta]'));
    this.buttonText = this.querySelector('[data-cta-text]');
    this.msg = this.querySelector('[data-msg]');
    this.image = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-image]'));
    this.priceEl = this.querySelector('[data-price]');

    /** @type {Record<number, string>} Only options with a single value start selected. */
    this.selected = {};
    this.querySelectorAll('.bf-tcard__swatch.is-on').forEach((el) => {
      const b = /** @type {HTMLElement} */ (el);
      this.selected[Number(b.dataset.option)] = b.dataset.value || '';
    });
    this.querySelectorAll('select[data-option]').forEach((el) => {
      const sel = /** @type {HTMLSelectElement} */ (el);
      if (sel.value) this.selected[Number(sel.dataset.option)] = sel.value;
    });

    this.addEventListener('click', this.onClick);
    // Only a real click on the button adds to the bag.
    this.button.addEventListener('click', this.onButtonClick);
    this.addEventListener('change', this.onChange);
    this.refresh();
  }

  get optionCount() {
    return this.variants[0]?.options.length || 0;
  }

  /** @returns {typeof this.variants[number] | undefined} */
  get variant() {
    if (this.button.dataset.variant) return this.variants[0];
    return this.variants.find((v) => v.options.every((value, i) => this.selected[i] === value));
  }

  /** @param {Event} event */
  onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const step = /** @type {HTMLElement | null} */ (target.closest('[data-qty-step]'));
    if (step) {
      const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-qty-input]'));
      if (input) input.value = String(Math.min(99, Math.max(1, (Number(input.value) || 1) + Number(step.dataset.qtyStep))));
      return;
    }
    const swatch = /** @type {HTMLElement | null} */ (target.closest('.bf-tcard__swatch'));
    if (swatch) {
      const index = Number(swatch.dataset.option);
      this.selected[index] = swatch.dataset.value || '';
      swatch.parentElement?.querySelectorAll('.bf-tcard__swatch').forEach((el) => {
        el.classList.toggle('is-on', el === swatch);
        el.setAttribute('aria-pressed', String(el === swatch));
      });
      const name = this.querySelector('[data-color-name]');
      if (name && index === this.colorIndex) name.textContent = swatch.dataset.value || '';
      this.refresh();
    }
  };

  /** @param {MouseEvent} event */
  onButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!event.isTrusted && !event.detail) return;
    this.onCta();
  };

  /** @param {Event} event */
  onChange = (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    if (!select.matches('select[data-option]')) return;
    this.selected[Number(select.dataset.option)] = select.value;
    this.refresh();
  };

  /** Update photo, price, and button for the current selection. */
  refresh() {
    this.setMsg('');

    // Photo follows the color, even before a size is chosen.
    if (this.colorIndex >= 0 && this.image && this.selected[this.colorIndex]) {
      const color = this.selected[this.colorIndex];
      const withImage = this.variants.find((v) => v.options[this.colorIndex] === color && v.image);
      if (withImage?.image && !this.image.src.startsWith(withImage.image.split('?')[0])) {
        this.image.classList.add('is-swapping');
        const next = new Image();
        next.onload = () => {
          if (!this.image) return;
          this.image.removeAttribute('srcset');
          this.image.src = withImage.image || '';
          this.image.classList.remove('is-swapping');
        };
        next.src = withImage.image;
      }
    }

    this.updateSizePrices();
    this.updateLinks();

    if (this.dataset.closed === 'true') return;

    const variant = this.variant;
    if (this.priceEl) this.priceEl.textContent = this.priceText(variant);
    if (this.dataset.personalized === 'true') {
      this.button.disabled = false;
      return;
    }
    // Same words as the product page: "Sold out" when it's out of stock, "Unavailable" when Shopify
    // can't sell it for another reason (e.g. no shipping set up for it)
    const pool = variant ? [variant] : this.variants;
    const blocked = !pool.some((v) => v.available);
    this.button.disabled = blocked;
    let text = this.dataset.cta || 'Add to bag';
    if (blocked) text = pool.every((v) => v.soldOut) ? 'Sold out' : 'Unavailable';
    if (this.buttonText) this.buttonText.textContent = text;
  }

  /**
   * The price for what's picked so far: exact once every option is chosen, otherwise the lowest
   * price among the matching variants ("From $x" when they differ), so it narrows as you pick.
   * @param {typeof this.variants[number] | undefined} variant
   * @returns {string}
   */
  priceText(variant) {
    if (variant) return variant.price;
    const matches = this.variants.filter((v) =>
      v.options.every((value, i) => !this.selected[i] || this.selected[i] === value)
    );
    const pool = matches.length ? matches : this.variants;
    const low = pool.reduce((a, b) => (b.cents < a.cents ? b : a), pool[0]);
    if (!low) return this.priceEl?.textContent || '';
    return pool.some((v) => v.cents !== low.cents) ? `From ${low.price}` : low.price;
  }

  /** Each dropdown choice shows its price ("M · $44.99") for the other options picked so far. */
  updateSizePrices() {
    this.querySelectorAll('select[data-option]').forEach((el) => {
      const select = /** @type {HTMLSelectElement} */ (el);
      const index = Number(select.dataset.option);
      Array.from(select.options).forEach((opt) => {
        if (!opt.value) return;
        const pool = this.variants.filter(
          (v) =>
            v.options[index] === opt.value &&
            v.options.every((value, i) => i === index || !this.selected[i] || this.selected[i] === value)
        );
        const label = opt.dataset.label || opt.value;
        const inStock = pool.filter((v) => v.available);
        const low = inStock.reduce((a, b) => (!a || b.cents < a.cents ? b : a), /** @type {any} */ (null));
        // Same wording as the product page: anything that can't be bought right now is "Unavailable"
        opt.textContent = low ? `${label} · ${low.price}` : `${label} · Unavailable`;
        opt.disabled = !low;
      });
    });

    // Colors with nothing in stock for the size picked so far get struck through
    this.querySelectorAll('.bf-tcard__swatch').forEach((el) => {
      const swatch = /** @type {HTMLElement} */ (el);
      const index = Number(swatch.dataset.option);
      const any = this.variants.some(
        (v) =>
          v.available &&
          v.options[index] === swatch.dataset.value &&
          v.options.every((value, i) => i === index || !this.selected[i] || this.selected[i] === value)
      );
      swatch.classList.toggle('is-unavailable', !any);
    });
  }

  /**
   * Point the photo and title links at the variant picked so far, so the product page opens with
   * the same color (and size) selected. With only a color picked, the first variant in that color.
   */
  updateLinks() {
    const match =
      this.variant ||
      this.variants.find((v) => v.available && v.options.every((value, i) => !this.selected[i] || this.selected[i] === value)) ||
      this.variants.find((v) => v.options.every((value, i) => !this.selected[i] || this.selected[i] === value));
    const picked = Object.values(this.selected).some(Boolean);
    this.querySelectorAll('a[href*="/products/"]').forEach((el) => {
      const link = /** @type {HTMLAnchorElement} */ (el);
      const url = new URL(link.href, window.location.origin);
      if (match && picked) url.searchParams.set('variant', String(match.id));
      else url.searchParams.delete('variant');
      link.href = url.pathname + url.search;
    });
  }

  /** @param {string} text */
  setMsg(text) {
    if (this.msg) this.msg.textContent = text;
  }

  /** @returns {string | null} the name of the first option still to choose */
  missingOption() {
    for (let i = 0; i < this.optionCount; i++) {
      if (!this.selected[i]) {
        const el = this.querySelector(`[data-option="${i}"]`);
        const label =
          el?.closest('label')?.querySelector('span')?.textContent ||
          el?.closest('fieldset')?.querySelector('legend')?.textContent?.split(':')[0] ||
          'option';
        return label.trim().toLowerCase();
      }
    }
    return null;
  }

  onCta() {
    const variant = this.variant;

    if (this.dataset.personalized === 'true') {
      const url = new URL(this.dataset.productUrl || '/', window.location.origin);
      if (variant) url.searchParams.set('variant', String(variant.id));
      // Carry the chosen quantity to the product page (bf-personalization.js fills it in)
      const qty = Number(/** @type {HTMLInputElement | null} */ (this.querySelector('[data-qty-input]'))?.value) || 1;
      if (qty > 1) url.searchParams.set('quantity', String(Math.min(99, qty)));
      window.location.href = url.toString();
      return;
    }

    if (!variant) {
      const missing = this.missingOption();
      this.setMsg(missing ? `Choose a ${missing} first.` : 'That combination is not available.');
      const el = /** @type {HTMLElement | null} */ (this.querySelector('select[data-option]:not([value]), select[data-option]'));
      if (el instanceof HTMLSelectElement && !el.value) el.focus();
      return;
    }
    if (!variant.available) return;
    const qtyInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-qty-input]'));
    const quantity = Math.min(99, Math.max(1, Number(qtyInput?.value) || 1));
    this.addToBag(variant.id, quantity);
  }

  /**
   * @param {number} variantId
   * @param {number} [quantity]
   */
  async addToBag(variantId, quantity = 1) {
    const label = this.buttonText?.textContent || '';
    this.button.disabled = true;
    if (this.buttonText) this.buttonText.textContent = 'Adding...';

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          // _bf_store: which store the item was bought from (for team sales reports)
          items: [{ id: variantId, quantity, ...(this.dataset.store && { properties: { _bf_store: this.dataset.store } }) }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      const cart = await (await fetch('/cart.js')).json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          sections: result.sections,
          source: 'bf-product-card',
          itemCount: quantity,
          didError: Boolean(result.status),
        },
      });

      if (result.status) {
        this.setMsg(result.description || result.message || 'Could not add that item.');
        if (this.buttonText) this.buttonText.textContent = label;
      } else if (this.buttonText) {
        this.buttonText.textContent = 'Added';
        setTimeout(() => {
          if (this.buttonText) this.buttonText.textContent = label;
        }, 1800);
      }
    } catch (error) {
      deferred.reject(error);
      this.setMsg('Something went wrong. Please try again.');
      if (this.buttonText) this.buttonText.textContent = label;
    } finally {
      this.button.disabled = false;
    }
  }
}

if (!customElements.get('bf-product-card')) customElements.define('bf-product-card', BfProductCard);

// Type pills + "Search this store" above a product grid: both filter the cards on the page.
/** @param {Element} wrap */
function bfApplyGridFilters(wrap) {
  const type = /** @type {HTMLElement} */ (wrap).dataset.bfType || '';
  const words = (/** @type {HTMLElement} */ (wrap).dataset.bfQuery || '').toLowerCase().split(/\s+/).filter(Boolean);
  let shown = 0;
  wrap.querySelectorAll('.bf-tgrid__item').forEach((el) => {
    const item = /** @type {HTMLElement} */ (el);
    const text = item.dataset.search || '';
    const match = (!type || item.dataset.type === type) && words.every((w) => text.includes(w));
    item.hidden = !match;
    if (match) shown++;
  });
  const count = wrap.querySelector('[data-bf-count]');
  if (count) count.textContent = `${shown} ${shown === 1 ? 'product' : 'products'}`;
  let empty = wrap.querySelector('.bf-tgrid__nomatch');
  if (!shown && !empty) {
    empty = document.createElement('p');
    empty.className = 'bf-tgrid__nomatch';
    empty.textContent = 'Nothing matches that. Try another word or tap All.';
    wrap.querySelector('.bf-tgrid')?.after(empty);
  }
  if (empty) /** @type {HTMLElement} */ (empty).hidden = shown > 0;
}

document.addEventListener('click', (event) => {
  const pill = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (event.target).closest?.('[data-bf-filter]'));
  if (!pill) return;
  const wrap = pill.closest('.bf-tgrid-wrap');
  if (!wrap) return;
  /** @type {HTMLElement} */ (wrap).dataset.bfType = pill.dataset.bfFilter || '';
  wrap.querySelectorAll('[data-bf-filter]').forEach((el) => {
    el.classList.toggle('is-on', el === pill);
    el.setAttribute('aria-pressed', String(el === pill));
  });
  bfApplyGridFilters(wrap);
});

document.addEventListener('input', (event) => {
  const input = /** @type {HTMLInputElement} */ (event.target);
  if (!input.matches?.('[data-bf-search]')) return;
  const wrap = input.closest('.bf-tgrid-wrap');
  if (!wrap) return;
  /** @type {HTMLElement} */ (wrap).dataset.bfQuery = input.value.trim();
  bfApplyGridFilters(wrap);
});
